import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { fetchJobsTable, fetchMyAssignments } from '../api/jobsApi';
import useOrgRole from './useOrgRole';

const HUB_URL = `${import.meta.env.VITE_API_URL}/hub/liverefresh`;

// Workers can't call GetJobsTable (ManagerUp policy — exposes margin
// figures). They see the same jobs via GetMyAssignments, which is
// scoped to their assignments and strips the money columns. We do the
// search/status/customer filtering client-side since per-Worker
// assignment counts are small.
function applyWorkerFilters(rows, filter) {
    let items = rows.map(a => ({
        id:           a.jobId,
        jobNumber:    a.jobNumber,
        title:        a.title,
        status:       a.status,
        currency:     a.currency,
        customerName: a.customerName,
        startDate:    a.startDate,
        endDate:      a.endDate,
    }));

    if (filter.statusFilter) {
        items = items.filter(j => j.status === filter.statusFilter);
    }

    if (filter.customerId) {
        // Worker assignments don't carry customerId on the row, so we can't
        // filter on it client-side. Workers don't see the customer detail
        // page anyway (the sidebar hides it), so this branch is unreachable
        // in practice — return [] to fail closed.
        return { items: [], totalRecords: 0 };
    }

    const term = filter.searchOptions?.searchTerm?.trim().toLowerCase();
    if (term) {
        items = items.filter(j =>
            (j.jobNumber    || '').toLowerCase().includes(term)
         || (j.title        || '').toLowerCase().includes(term)
         || (j.customerName || '').toLowerCase().includes(term));
    }

    return { items, totalRecords: items.length };
}

export default function useJobs(filter) {
    const queryClient = useQueryClient();
    const role = useOrgRole();
    const isWorker = role === 'Worker';

    const query = useQuery({
        // Wait until role is resolved — otherwise the very first render
        // dispatches a GetJobsTable call before useOrgRole returns
        // 'Worker', which 403s and pollutes Sentry.
        enabled: role !== undefined,
        queryKey: ['jobs', isWorker ? 'worker' : 'manager', filter],
        queryFn: async () => {
            if (isWorker) {
                const rows = await fetchMyAssignments();
                return applyWorkerFilters(rows ?? [], filter);
            }
            return fetchJobsTable(filter);
        },
        placeholderData: (prev) => prev,
    });

    // See useQuotes.js — same scoping contract.
    const scopedCustomerId = filter.customerId ?? null;

    useEffect(() => {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                // Clerk issues short-lived tokens; SignalR calls this factory
                // on connect and on reconnect, so we always hand it a fresh one.
                accessTokenFactory: () =>
                    window.Clerk.session.getToken({ template: 'marginpal-api' }),
            })
            .withAutomaticReconnect()
            .build();

        connection.on('UpdateJobTable', (eventCustomerId) => {
            // Scoped tables only refresh when the broadcast is for their
            // customer (or a global null event). Global tables always refresh.
            if (!scopedCustomerId || eventCustomerId == null || eventCustomerId === scopedCustomerId) {
                queryClient.invalidateQueries({ queryKey: ['jobs'] });
            }
        });

        connection.start().catch(err => console.error('SignalR connection error:', err));

        return () => {
            connection.stop();
        };
    }, [queryClient, scopedCustomerId]);

    return query;
}
